import Ga4Service from '../utils/Ga4Service'
import { EVENTS } from '../constants'

type EventCallbackType = {
  eventCallback?: () => void
}

const sendGa4ContactEvent = ({
  province,
  method,
  eventCallback,
}: EventCallbackType & {
  province: string
  method: 'email' | 'whatsapp'
}) => {
  Ga4Service.send({
    event: EVENTS.CONTACT,
    region_action: province,
    method,
    eventCallback,
  })
}

const sendGa4RecommendedClickEvent = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.RESTRICTED_RECOMENDED_CLICK,
    eventCallback: eventCallback,
  })
}

const sendGa4KeepSearchEvent = ({ eventCallback }: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.KEEP_SEARCHING,
    eventCallback: eventCallback,
  })
}

const sendGa4HolidaysClickEvent = ({
  province,
  title,
  eventCallback,
}: EventCallbackType & {
  province: string
  title: string
}) => {
  Ga4Service.send({
    event: EVENTS.HOLIDAYS_CLICK,
    region_action: province,
    section: title,
    eventCallback,
  })
}

const sendGa4RoSuccessBlogClickEvent = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.RO_SUCCESS_BLOG_CLICK,
    eventCallback,
  })
}

const sendGa4RoBeginCheckoutEvent = ({
  cottage,
  amount,
  eventCallback,
}: EventCallbackType & {
  cottage: {
    name: string
    id: string
    province: string
  }
  amount: number
}) => {
  Ga4Service.send({
    event: 'begin_checkout',
    type: 'booking',
    region_action: cottage.province,
    eventCallback: eventCallback,
    ecommerce: {
      currency: 'EUR',
      value: amount,
      transaction_id: new Date().getTime(),
      items: [
        {
          item_name: cottage.name,
          item_id: cottage.id,
          price: amount,
          quantity: '1',
        },
      ],
    },
  })
}

const sendGa4RoPaymentSuccessEvent = ({
  cottage,
  amount,
  eventCallback,
}: EventCallbackType & {
  cottage: {
    name: string
    id: string
    province: string
  }
  amount: number
}) => {
  Ga4Service.send({
    event: 'purchase',
    type: 'booking',
    region_action: cottage.province,
    eventCallback: eventCallback,
    ecommerce: {
      currency: 'EUR',
      value: amount,
      transaction_id: new Date().getTime(),
      items: [
        {
          item_name: cottage.name,
          item_id: cottage.id,
          price: amount,
          quantity: '1',
        },
      ],
    },
  })
}

const sendGa4RoContactClickEvent = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.RO_CONTACT_CLICK,
    eventCallback,
  })
}

const sendGa4PrebookingEvent = ({
  cottage,
  amount,
  eventCallback,
}: EventCallbackType & {
  cottage: {
    name: string
    id: string
    province: string
  }
  amount: number
}) => {
  Ga4Service.send({
    event: 'purchase',
    type: 'pre_booking',
    region_action: cottage.province,
    method: 'email',
    eventCallback: eventCallback,
    ecommerce: {
      currency: 'EUR',
      value: amount,
      transaction_id: new Date().getTime(),
      items: [
        {
          item_name: cottage.name,
          item_id: cottage.id,
          price: amount,
          quantity: '1',
        },
      ],
    },
  })
}

const sendGa4CottagePricesLink = ({
  cottage,
  eventCallback,
}: EventCallbackType & {
  cottage: {
    name: string
  }
}) => {
  Ga4Service.send({
    event: EVENTS.COTTAGE_VIEW_PRICES_LINK,
    cottage_name: cottage.name,
    eventCallback: eventCallback,
  })
}

const sendGa4ViewPhone = ({
  region_action,
  eventCallback,
}: EventCallbackType & {
  region_action: string
}) => {
  Ga4Service.send({
    event: EVENTS.VIEW_PHONE,
    region_action: region_action,
    eventCallback: eventCallback,
  })
}

const sendGa4ClickPhone = ({
  region_action,
  eventCallback,
}: EventCallbackType & {
  region_action: string
}) => {
  Ga4Service.send({
    event: EVENTS.CLICK_PHONE,
    region_action: region_action,
    eventCallback: eventCallback,
  })
}

const sendCottageConversionAll = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.COTTAGE_CONVERSION_ALL,
    eventCallback,
  })
}

const sendHomeSimpleBannerClick = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.HOME_SIMPLE_BANNER_CLICK,
    eventCallback,
  })
}

const sendHomeMediaBannerClick = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.HOME_MEDIA_BANNER_CLICK,
    eventCallback,
  })
}

const sendHomeFeaturedDestinationsClick = ({
  index,
  eventCallback,
}: EventCallbackType & {
  index: number
}) => {
  Ga4Service.send({
    event: EVENTS.HOME_FEATURED_DESTINATIONS_CLICK,
    card: index,
    eventCallback,
  })
}

const sendHomeImageBannerClick = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.HOME_IMAGE_BANNER_CLICK,
    eventCallback,
  })
}

const sendGa4PricesGoBack = () => {
  Ga4Service.send({
    event: EVENTS.PRICES_GO_BACK,
  })
}

const sendGa4PublishYourPropertyClick = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.PUBLISH_YOUR_PROPERTY_CLICK,
    eventCallback,
  })
}

const sendGa4RegisterOwnerEvent = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.REGISTER_OWNER,
    eventCallback,
  })
}

const sendGa4ImproveYourReservationsHeroClick = ({
  index,
  eventCallback,
}: {
  index: number
  eventCallback?: () => void
}) => {
  Ga4Service.send({
    event: EVENTS.IMPROVE_YOUR_RESERVATIONS_HERO_CLICK.replace(
      '{index}',
      index.toString(),
    ),
    eventCallback,
  })
}

const sendGa4ImproveYourReservationsCTAClick = ({
  index,
  eventCallback,
}: {
  index: number
  eventCallback?: () => void
}) => {
  Ga4Service.send({
    event: EVENTS.IMPROVE_YOUR_RESERVATIONS_CTA_CLICK.replace(
      '{index}',
      index.toString(),
    ),
    eventCallback,
  })
}

const sendGa4ImproveYourReservationsAlertClick = ({
  index,
  eventCallback,
}: {
  index: number
  eventCallback?: () => void
}) => {
  Ga4Service.send({
    event: EVENTS.IMPROVE_YOUR_RESERVATIONS_ALERT_CLICK.replace(
      '{index}',
      index.toString(),
    ),
    eventCallback,
  })
}

const sendGa4ImproveYourReservationsFormSubmit = ({
  eventCallback,
}: EventCallbackType = {}) => {
  Ga4Service.send({
    event: EVENTS.IMPROVE_YOUR_RESERVATIONS_FORM_SUBMIT,
    eventCallback,
  })
}

export {
  sendGa4ContactEvent,
  sendGa4RecommendedClickEvent,
  sendGa4KeepSearchEvent,
  sendGa4HolidaysClickEvent,
  sendGa4RoSuccessBlogClickEvent,
  sendGa4RoBeginCheckoutEvent,
  sendGa4RoPaymentSuccessEvent,
  sendGa4RoContactClickEvent,
  sendGa4PrebookingEvent,
  sendGa4CottagePricesLink,
  sendGa4ViewPhone,
  sendGa4ClickPhone,
  sendCottageConversionAll,
  sendHomeSimpleBannerClick,
  sendHomeMediaBannerClick,
  sendHomeFeaturedDestinationsClick,
  sendHomeImageBannerClick,
  sendGa4PricesGoBack,
  sendGa4PublishYourPropertyClick,
  sendGa4RegisterOwnerEvent,
  sendGa4ImproveYourReservationsHeroClick,
  sendGa4ImproveYourReservationsCTAClick,
  sendGa4ImproveYourReservationsAlertClick,
  sendGa4ImproveYourReservationsFormSubmit,
}
